import { FC, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import { useAuth } from '../../../../../auth'
import { EstudoDicom } from '../../../../atendimento/core/_models'
import { getMetadataEstudoDicom } from '../../../../atendimento/core/_requests'
import { estudoTemCalibracao } from './calibracao'
import { valoresDescricaoEstudo } from './descricaoEstudo'
import { montarUrlViewer } from './viewerUrl'
import styles from './VisualizadorDicom.module.css'

type Props = {
  idAtendimento: number
  /** Estudo aberto; sem estudo o modal fica fechado e o iframe sai do DOM. */
  estudo?: EstudoDicom
  onHide: () => void
}

const ID_TITULO = 'visualizador-dicom-titulo'
/** Mensagem que o app-config do OHIF manda no Esc duplo (scripts/viewer/app-config.lauduz.js). */
export const MENSAGEM_FECHAR_VIEWER = 'lauduz:viewer:fechar'
const DEZ_MINUTOS = 10 * 60 * 1000

export const VisualizadorDicomModal: FC<Props> = ({ idAtendimento, estudo, onHide }) => {
  const { t } = useT()
  const { auth, currentUser } = useAuth()
  const sessaoAtiva = !!auth?.accessToken && !!currentUser
  const aberto = !!estudo && sessaoAtiva
  const uid = estudo?.studyInstanceUid ?? ''

  // Logout não desmonta o modal global: o viewer fecha sozinho quando a sessão some.
  useEffect(() => {
    if (estudo && !sessaoAtiva) onHide()
  }, [estudo, sessaoAtiva, onHide])

  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Esc duplo com o foco dentro do OHIF: o primeiro Esc continua dele (cancela a medida em andamento).
  useEffect(() => {
    if (!aberto) return undefined
    const aoReceber = (evento: MessageEvent) => {
      if (evento.origin !== window.location.origin) return
      const quadro = iframeRef.current?.contentWindow
      if (!quadro || evento.source !== quadro) return
      if (evento.data?.type === MENSAGEM_FECHAR_VIEWER) onHide()
    }
    window.addEventListener('message', aoReceber)
    return () => window.removeEventListener('message', aoReceber)
  }, [aberto, onHide])

  // Sentinelas de foco: o Tab dá a volta dentro do modal em vez de sair para o atendimento.
  const focarIframe = () => iframeRef.current?.focus()
  const focarFechar = () =>
    document
      .querySelector<HTMLButtonElement>(`[aria-labelledby="${ID_TITULO}"] .btn-close`)
      ?.focus()

  const metadata = useQuery(
    ['dicom-metadata', idAtendimento, uid],
    () => getMetadataEstudoDicom(idAtendimento, uid),
    { enabled: aberto, staleTime: DEZ_MINUTOS, retry: false, refetchOnWindowFocus: false }
  )
  // Enquanto carrega ou se falhar, o aviso aparece: na dúvida a medida não é tratada como mm.
  const calibrado = metadata.isSuccess && estudoTemCalibracao(metadata.data)
  const src = estudo ? montarUrlViewer(idAtendimento, estudo.studyInstanceUid) : null
  const descricao = estudo ? valoresDescricaoEstudo(estudo, t) : undefined

  return (
    <Modal
      show={aberto}
      onHide={onHide}
      size='xl'
      className={styles.modal}
      dialogClassName={styles.dialogo}
      backdropClassName={styles.fundo}
      aria-labelledby={ID_TITULO}
      restoreFocus
      keyboard
    >
      <span tabIndex={0} className={styles.sentinela} onFocus={focarIframe} />
      <Modal.Header closeButton closeLabel={t('exames_imagem.viewer.fechar')}>
        <div className={styles.cabecalho}>
          <Modal.Title as='h2' id={ID_TITULO} className='fs-5 fw-bold text-gray-900 mb-0'>
            {t('exames_imagem.viewer.titulo')}
          </Modal.Title>
          {descricao && (
            <span className='text-gray-700 fs-7'>
              {t('exames_imagem.viewer.descricao', descricao)}
            </span>
          )}
          <span className='text-gray-700 fs-7'>{t('exames_imagem.viewer.dica_esc')}</span>
        </div>
      </Modal.Header>
      {!calibrado && (
        <div className={styles.avisoCalibracao} role='note'>
          <i className='fas fa-ruler fs-7 me-2' style={{ color: 'inherit' }} aria-hidden='true' />
          <strong className='me-1'>{t('exames_imagem.viewer.sem_calibracao_titulo')}</strong>
          <span>{t('exames_imagem.viewer.sem_calibracao')}</span>
        </div>
      )}
      <Modal.Body className='p-0'>
        {aberto && src && descricao && (
          <iframe
            ref={iframeRef}
            className={styles.iframe}
            src={src}
            title={t('exames_imagem.viewer.iframe_title', descricao)}
            sandbox='allow-scripts allow-same-origin'
            allow='fullscreen'
            referrerPolicy='no-referrer'
          />
        )}
      </Modal.Body>
      <span tabIndex={0} className={styles.sentinela} onFocus={focarFechar} />
    </Modal>
  )
}
